// /app/frontend/src/pages/MyFavoritesPage.jsx
// User's saved color favorites page

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Trash2, Package, ExternalLink, Loader2, 
  ChevronRight, Search, Grid, List, Send, Scale, X, Check, Share2, Copy, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { resolveImageUrl } from '../lib/imageCache';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { brandLabel } from '../lib/brandNames';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyFavoritesPage = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [removingId, setRemovingId] = useState(null);
  
  // Comparison feature state
  const [compareMode, setCompareMode] = useState(false);
  const [compareColors, setCompareColors] = useState([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    const currentToken = localStorage.getItem('apollo_token');
    if (!currentToken) {
      setLoading(false);
      navigate('/login');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/user/favorite-colors`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setFavorites(response.data?.colors || []);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load favorites');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (colorId) => {
    const currentToken = localStorage.getItem('apollo_token');
    setRemovingId(colorId);
    try {
      await axios.delete(`${API_URL}/api/user/favorite-colors/${colorId}`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setFavorites(prev => prev.filter(f => f.color_id !== colorId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove favorite');
    } finally {
      setRemovingId(null);
    }
  };

  const viewInConfigurator = (favorite) => {
    // Navigate to the product configurator with the color pre-selected
    // Using roller-shades as default, could be enhanced to detect product type
    navigate(`/products/roller-shades?brand=${encodeURIComponent(favorite.brand_name)}&collection=${encodeURIComponent(favorite.collection_name)}&material=${encodeURIComponent(favorite.material_name)}&color=${favorite.color_id}`);
  };

  const requestAllAsSamples = () => {
    // Transform favorites to sample request format
    const samplesData = favorites.map(fav => ({
      color_id: fav.color_id,
      color_name: fav.color_name,
      color_code: fav.color_code || '',
      hex_color: fav.hex_color || '',
      image_url: fav.image_url || '',
      material_name: fav.material_name || '',
      collection_name: fav.collection_name || '',
      brand_name: fav.brand_name || ''
    }));
    
    // Store in localStorage for the request page
    localStorage.setItem('sampleRequestColors', JSON.stringify(samplesData));
    
    // Navigate to request page
    navigate('/request-samples');
    toast.success(`${favorites.length} color${favorites.length !== 1 ? 's' : ''} added to sample request`);
  };

  // Comparison functions
  const toggleCompareColor = (favorite) => {
    setCompareColors(prev => {
      const exists = prev.find(c => c.color_id === favorite.color_id);
      if (exists) {
        return prev.filter(c => c.color_id !== favorite.color_id);
      }
      return [...prev, favorite];
    });
  };

  const isInCompare = (colorId) => {
    return compareColors.some(c => c.color_id === colorId);
  };

  const openCompareModal = () => {
    if (compareColors.length < 2) {
      toast.error('Select at least 2 colors to compare');
      return;
    }
    setCompareModalOpen(true);
  };

  const clearCompare = () => {
    setCompareColors([]);
    setCompareMode(false);
  };

  const filteredFavorites = favorites.filter(fav => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      fav.color_name?.toLowerCase().includes(search) ||
      fav.material_name?.toLowerCase().includes(search) ||
      fav.collection_name?.toLowerCase().includes(search) ||
      brandLabel(fav.brand_name)?.toLowerCase().includes(search) ||
      fav.brand_name?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="py-8">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="w-8 h-8 text-red-500 fill-red-500" />
              <h1 className="text-3xl font-bold text-neutral-900">My Favorites</h1>
            </div>
            <p className="text-neutral-500">
              Your saved fabric colors. Click on any color to view it in the configurator.
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Search favorites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {favorites.length > 1 && (
                <>
                  {compareMode ? (
                    <>
                      <Button
                        onClick={openCompareModal}
                        disabled={compareColors.length < 2}
                        className="bg-blue-600 hover:bg-blue-700 gap-2"
                        data-testid="compare-selected-btn"
                      >
                        <Scale className="w-4 h-4" />
                        Compare ({compareColors.length})
                      </Button>
                      <Button
                        variant="outline"
                        onClick={clearCompare}
                        className="gap-2"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setCompareMode(true)}
                      className="gap-2"
                      data-testid="compare-mode-btn"
                    >
                      <Scale className="w-4 h-4" />
                      Compare
                    </Button>
                  )}
                </>
              )}
              {favorites.length > 0 && !compareMode && (
                <Button
                  onClick={requestAllAsSamples}
                  className="bg-amber-600 hover:bg-amber-700 gap-2"
                  data-testid="request-all-samples-btn"
                >
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">Request All as Samples</span>
                  <span className="sm:hidden">Request Samples</span>
                </Button>
              )}
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Compare Mode Instructions */}
          {compareMode && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <Scale className="w-4 h-4 inline mr-2" />
              Select colors to compare (click the checkmark icon). Min 2 colors required.
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : filteredFavorites.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-neutral-200">
              <Heart className="w-16 h-16 mx-auto text-neutral-200 mb-4" />
              <h2 className="text-xl font-semibold text-neutral-700 mb-2">
                {searchTerm ? 'No matches found' : 'No favorites yet'}
              </h2>
              <p className="text-neutral-500 mb-6">
                {searchTerm 
                  ? 'Try a different search term' 
                  : 'Browse our fabric collections and save your favorite colors'}
              </p>
              {!searchTerm && (
                <Button 
                  onClick={() => navigate('/products/roller-shades')}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Browse Fabrics
                </Button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <AnimatePresence>
                {filteredFavorites.map((favorite) => (
                  <motion.div
                    key={favorite.color_id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className={`bg-white rounded-xl border overflow-hidden group hover:shadow-lg transition-shadow ${
                      isInCompare(favorite.color_id) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-neutral-200'
                    }`}
                  >
                    {/* Color Swatch */}
                    <div 
                      className="aspect-square relative cursor-pointer"
                      onClick={() => compareMode ? toggleCompareColor(favorite) : viewInConfigurator(favorite)}
                      style={{ backgroundColor: favorite.hex_color || '#E5E5E5' }}
                    >
                      {favorite.image_url && (
                        <img 
                          src={resolveImageUrl(favorite.image_url, 256)} 
                          alt={favorite.color_name}
                          className="w-full h-full object-cover"
                        />
                      )}
                      
                      {/* Overlay */}
                      <div className={`absolute inset-0 transition-colors flex items-center justify-center ${
                        compareMode 
                          ? (isInCompare(favorite.color_id) ? 'bg-blue-500/30' : 'bg-black/0 hover:bg-blue-500/20')
                          : 'bg-black/0 group-hover:bg-black/40'
                      }`}>
                        {compareMode ? (
                          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                            isInCompare(favorite.color_id) 
                              ? 'bg-blue-500 border-blue-500 text-white' 
                              : 'bg-white/80 border-neutral-300'
                          }`}>
                            {isInCompare(favorite.color_id) && <Check className="w-5 h-5" />}
                          </div>
                        ) : (
                          <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                      
                      {/* Heart badge */}
                      <div className="absolute top-2 right-2">
                        <Heart className="w-5 h-5 text-red-500 fill-red-500 drop-shadow-md" />
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="p-3">
                      <h3 className="font-semibold text-neutral-900 truncate">{favorite.color_name}</h3>
                      <p className="text-xs text-neutral-500 truncate mt-0.5">
                        {brandLabel(favorite.brand_name)} → {favorite.collection_name}
                      </p>
                      <p className="text-xs text-neutral-400 truncate">
                        {favorite.material_name}
                      </p>
                      
                      {/* Actions */}
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-8 text-xs"
                          onClick={() => viewInConfigurator(favorite)}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs text-red-600 hover:bg-red-50"
                          onClick={() => removeFavorite(favorite.color_id)}
                          disabled={removingId === favorite.color_id}
                        >
                          {removingId === favorite.color_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            /* List View */
            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
              <AnimatePresence>
                {filteredFavorites.map((favorite, index) => (
                  <motion.div
                    key={favorite.color_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className={`flex items-center gap-4 p-4 hover:bg-neutral-50 transition-colors ${
                      index !== filteredFavorites.length - 1 ? 'border-b border-neutral-100' : ''
                    } ${isInCompare(favorite.color_id) ? 'bg-blue-50' : ''}`}
                  >
                    {/* Compare checkbox */}
                    {compareMode && (
                      <button
                        onClick={() => toggleCompareColor(favorite)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isInCompare(favorite.color_id)
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : 'border-neutral-300'
                        }`}
                      >
                        {isInCompare(favorite.color_id) && <Check className="w-4 h-4" />}
                      </button>
                    )}
                    
                    {/* Color Swatch */}
                    <div 
                      className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer border border-neutral-200"
                      onClick={() => viewInConfigurator(favorite)}
                      style={{ backgroundColor: favorite.hex_color || '#E5E5E5' }}
                    >
                      {favorite.image_url && (
                        <img 
                          src={resolveImageUrl(favorite.image_url, 256)} 
                          alt={favorite.color_name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-neutral-900">{favorite.color_name}</h3>
                      <div className="flex items-center gap-1 text-sm text-neutral-500 mt-0.5">
                        <span>{brandLabel(favorite.brand_name)}</span>
                        <ChevronRight className="w-3 h-3" />
                        <span>{favorite.collection_name}</span>
                        <ChevronRight className="w-3 h-3" />
                        <span>{favorite.material_name}</span>
                      </div>
                      {favorite.color_code && (
                        <p className="text-xs text-neutral-400 mt-1">Code: {favorite.color_code}</p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewInConfigurator(favorite)}
                        className="gap-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View in Configurator
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeFavorite(favorite.color_id)}
                        disabled={removingId === favorite.color_id}
                        className="text-red-600 hover:bg-red-50"
                      >
                        {removingId === favorite.color_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Stats */}
          {!loading && favorites.length > 0 && (
            <div className="mt-8 text-center text-sm text-neutral-400">
              {filteredFavorites.length} of {favorites.length} favorite{favorites.length !== 1 ? 's' : ''} shown
            </div>
          )}
        </div>
      </main>
      
      <Footer />

      {/* Comparison Modal */}
      <Dialog open={compareModalOpen} onOpenChange={setCompareModalOpen}>
        <DialogContent className="max-w-6xl p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-neutral-200">
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-blue-600" />
              Compare Fabrics ({compareColors.length} colors)
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-4 overflow-x-auto">
            <div className="flex gap-4 min-w-max">
              {compareColors.map((color) => (
                <div 
                  key={color.color_id}
                  className="w-64 flex-shrink-0 bg-white rounded-lg border border-neutral-200 overflow-hidden"
                >
                  {/* Color Swatch */}
                  <div 
                    className="aspect-square w-full"
                    style={{ backgroundColor: color.hex_color || '#E5E5E5' }}
                  >
                    {color.image_url && (
                      <img 
                        src={resolveImageUrl(color.image_url, 256)} 
                        alt={color.color_name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  
                  {/* Details */}
                  <div className="p-4 space-y-2">
                    <h3 className="font-bold text-lg text-neutral-900">{color.color_name}</h3>
                    
                    {color.color_code && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Code</span>
                        <span className="font-medium">{color.color_code}</span>
                      </div>
                    )}
                    
                    {color.hex_color && (
                      <div className="flex justify-between text-sm items-center">
                        <span className="text-neutral-500">Hex</span>
                        <span className="font-mono text-xs bg-neutral-100 px-2 py-1 rounded">{color.hex_color}</span>
                      </div>
                    )}
                    
                    <div className="pt-2 border-t border-neutral-100">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Brand</span>
                        <span className="font-medium">{brandLabel(color.brand_name)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Collection</span>
                        <span className="font-medium">{color.collection_name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Material</span>
                        <span className="font-medium">{color.material_name}</span>
                      </div>
                    </div>
                    
                    {/* Remove from comparison */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-2 text-red-600 hover:bg-red-50"
                      onClick={() => toggleCompareColor(color)}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Footer Actions */}
          <div className="p-4 border-t border-neutral-200 bg-neutral-50">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              {/* Share Options */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Create shareable URL with color data
                    const colorData = btoa(JSON.stringify(compareColors.map(c => ({
                      color_id: c.color_id,
                      color_name: c.color_name,
                      color_code: c.color_code,
                      hex_color: c.hex_color,
                      image_url: c.image_url,
                      brand_name: c.brand_name,
                      collection_name: c.collection_name,
                      material_name: c.material_name
                    }))));
                    const shareUrl = `${window.location.origin}/compare-fabrics?colors=${compareColors.map(c => c.color_id).join(',')}&data=${colorData}`;
                    navigator.clipboard.writeText(shareUrl);
                    toast.success('Comparison link copied to clipboard!');
                  }}
                  className="gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy Link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Create email with comparison details
                    const colorData = btoa(JSON.stringify(compareColors.map(c => ({
                      color_id: c.color_id,
                      color_name: c.color_name,
                      color_code: c.color_code,
                      hex_color: c.hex_color,
                      image_url: c.image_url,
                      brand_name: c.brand_name,
                      collection_name: c.collection_name,
                      material_name: c.material_name
                    }))));
                    const shareUrl = `${window.location.origin}/compare-fabrics?colors=${compareColors.map(c => c.color_id).join(',')}&data=${colorData}`;
                    const subject = encodeURIComponent(`Apollo Shades - Fabric Comparison (${compareColors.length} colors)`);
                    const body = encodeURIComponent(`Check out this fabric comparison I'm considering:\n\n${compareColors.map(c => `• ${c.color_name} (${brandLabel(c.brand_name)} - ${c.material_name})`).join('\n')}\n\nView comparison: ${shareUrl}`);
                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                  }}
                  className="gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </Button>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Request all comparison colors as samples
                    const samplesData = compareColors.map(c => ({
                      color_id: c.color_id,
                      color_name: c.color_name,
                      color_code: c.color_code || '',
                      hex_color: c.hex_color || '',
                      image_url: c.image_url || '',
                      material_name: c.material_name || '',
                      collection_name: c.collection_name || '',
                      brand_name: c.brand_name || ''
                    }));
                    localStorage.setItem('sampleRequestColors', JSON.stringify(samplesData));
                    navigate('/request-samples');
                    toast.success(`${compareColors.length} colors added to sample request`);
                  }}
                  className="gap-2"
                >
                  <Package className="w-4 h-4" />
                  Request Samples
                </Button>
                <Button
                  onClick={() => setCompareModalOpen(false)}
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyFavoritesPage;
